/**
 * Structured Feedback Display Component
 * Displays AI-generated feedback in a user-friendly format with:
 * - Summary at the top
 * - Individual criterion cards with scores
 * - Grand total at the bottom
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Card,
  CardContent,
  Chip,
  Stack,
  LinearProgress,
  Alert,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { CodeSearchContext } from '../../contexts/CodeSearchContext';
import { getBestSearchKeyword } from '../../utils/criterionSearchPatterns';

interface Criterion {
  key?: string;
  name: string;
  score: number;
  max_score: number;
  feedback: string;
}

interface StructuredFeedbackData {
  score: number;
  summary?: string;
  criteria?: Criterion[];
}

interface StructuredFeedbackProps {
  feedback: string | StructuredFeedbackData;
  totalScore?: number;
}

const stripKeyPrefix = (name: string, key?: string): string => {
  if (!key) return name;
  const escaped = key.replace(/\./g, '\\.');
  const stripped = name.replace(new RegExp(`^Q?${escaped}\\s*[—\\-–]?\\s*`, 'i'), '').trim();
  return stripped || name;
};

interface CriterionCardProps {
  criterion: Criterion;
  index: number;
  getCriterionPercentage: (score: number, maxScore: number) => number;
  getScoreColor: (pct: number) => 'success' | 'warning' | 'error';
  getScoreIcon: (pct: number) => React.ReactNode;
}

const CriterionCard: React.FC<CriterionCardProps> = ({ criterion, index, getCriterionPercentage, getScoreColor, getScoreIcon }) => {
  const percentage = getCriterionPercentage(criterion.score, criterion.max_score);
  const color = getScoreColor(percentage);
  const searchContext = React.useContext(CodeSearchContext);

  const handleChipClick = () => {
    if (!searchContext || !criterion.key) return;
    if (/^\d+$/.test(criterion.key)) {
      // Theory: scroll directly to matching Q&A question number
      searchContext.setSearchTerm('');
      searchContext.setQuestionFilter(parseInt(criterion.key));
    } else {
      // Programming: push a keyword search so the right code block highlights and auto-scrolls
      const keyword = getBestSearchKeyword(criterion.name, criterion.feedback);
      searchContext.setSearchTerm(keyword);
      searchContext.setQuestionFilter(null);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Tooltip title="Go to question in Q&A" placement="top">
            <Chip
              label={criterion.key ? `Q${criterion.key}` : `Q${index + 1}`}
              size="small"
              color="primary"
              variant="outlined"
              onClick={searchContext ? handleChipClick : undefined}
              sx={searchContext ? { cursor: 'pointer', '&:hover': { bgcolor: 'primary.main', color: 'white' } } : {}}
            />
          </Tooltip>
          <Typography variant="subtitle2" fontWeight="bold">
            {stripKeyPrefix(criterion.name, criterion.key)}
          </Typography>
        </Stack>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          {getScoreIcon(percentage)}
          <Typography variant="h6" fontWeight="bold" color={`${color}.main`} sx={{ whiteSpace: 'nowrap' }}>
            {criterion.score} / {criterion.max_score}
          </Typography>
          <Chip label={`${percentage.toFixed(0)}%`} color={color} size="small" />
        </Box>
        <LinearProgress variant="determinate" value={percentage} sx={{ mb: 2, height: 6, borderRadius: 3 }} color={color} />
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
          <Typography variant="body2">{criterion.feedback}</Typography>
        </Paper>
      </CardContent>
    </Card>
  );
};

const StructuredFeedback: React.FC<StructuredFeedbackProps> = ({ feedback, totalScore }) => {
  // Parse feedback if it's a string
  const parsedFeedback = React.useMemo(() => {
    console.log('StructuredFeedback received:', feedback);
    if (typeof feedback === 'string') {
      try {
        const parsed = JSON.parse(feedback) as StructuredFeedbackData;
        console.log('Parsed feedback:', parsed);
        return parsed;
      } catch (e) {
        console.log('Failed to parse feedback, using old format');
        // If parsing fails, return old format indicator
        return null;
      }
    }
    console.log('Feedback is already an object:', feedback);
    return feedback as StructuredFeedbackData;
  }, [feedback]);

  // If old pipe-delimited format, show it as plain text
  if (!parsedFeedback || !parsedFeedback.criteria) {
    return (
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {typeof feedback === 'string' ? feedback : JSON.stringify(feedback, null, 2)}
        </Typography>
      </Paper>
    );
  }

  // Calculate percentage for each criterion
  const getCriterionPercentage = (score: number, maxScore: number) => {
    return maxScore > 0 ? (score / maxScore) * 100 : 0;
  };

  // Get color based on percentage
  const getScoreColor = (percentage: number): 'success' | 'warning' | 'error' => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };

  // Get icon based on percentage
  const getScoreIcon = (percentage: number) => {
    if (percentage >= 80) return <CheckCircleIcon fontSize="small" />;
    if (percentage >= 60) return <WarningIcon fontSize="small" />;
    return <ErrorIcon fontSize="small" />;
  };

  const totalMaxScore = parsedFeedback.criteria.reduce((sum, c) => sum + c.max_score, 0);
  const overallPercentage = totalMaxScore > 0 ? (parsedFeedback.score / totalMaxScore) * 100 : 0;

  // Split into theory (plain integer keys) vs programming (dotted keys)
  const theoryCriteria = parsedFeedback.criteria.filter(c => c.key && /^\d+$/.test(c.key));
  const programmingCriteria = parsedFeedback.criteria.filter(c => !c.key || /\./.test(c.key));
  const hasGroups = theoryCriteria.length > 0 && programmingCriteria.length > 0;

  return (
    <Box>
      {/* Overall Summary */}
      {parsedFeedback.summary && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Overall Assessment
          </Typography>
          <Typography variant="body2">
            {parsedFeedback.summary}
          </Typography>
        </Alert>
      )}

      {/* Grand Total Score */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50', border: 1, borderColor: 'primary.main' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" color="primary">
            Grand Total
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {getScoreIcon(overallPercentage)}
            <Typography variant="h5" fontWeight="bold" color="primary">
              {parsedFeedback.score} / {totalMaxScore}
            </Typography>
            <Chip
              label={`${overallPercentage.toFixed(0)}%`}
              color={getScoreColor(overallPercentage)}
              size="small"
            />
          </Box>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={overallPercentage}
          sx={{ mt: 1, height: 8, borderRadius: 4 }}
          color={getScoreColor(overallPercentage)}
        />
      </Paper>

      {/* Individual Criteria */}
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Detailed Feedback by Criterion
      </Typography>

      {hasGroups ? (
        <Box>
          {/* Theory section */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
            Theory — Q1–Q{theoryCriteria.length} ({theoryCriteria.length} questions)
          </Typography>
          <Stack spacing={2} sx={{ mb: 3 }}>
            {theoryCriteria.map((criterion, index) => <CriterionCard key={criterion.key || index} criterion={criterion} index={index} getCriterionPercentage={getCriterionPercentage} getScoreColor={getScoreColor} getScoreIcon={getScoreIcon} />)}
          </Stack>

          {/* Programming section */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
            Programming — {programmingCriteria.length} sub-criteria across {new Set(programmingCriteria.map(c => c.key?.split('.')[0])).size} questions
          </Typography>
          <Stack spacing={2}>
            {programmingCriteria.map((criterion, index) => <CriterionCard key={criterion.key || index} criterion={criterion} index={index} getCriterionPercentage={getCriterionPercentage} getScoreColor={getScoreColor} getScoreIcon={getScoreIcon} />)}
          </Stack>
        </Box>
      ) : (
        <Stack spacing={2}>
          {parsedFeedback.criteria.map((criterion, index) => <CriterionCard key={criterion.key || index} criterion={criterion} index={index} getCriterionPercentage={getCriterionPercentage} getScoreColor={getScoreColor} getScoreIcon={getScoreIcon} />)}
        </Stack>
      )}

      {/* Summary Table */}
      <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Score Breakdown
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Stack spacing={0.5}>
          {parsedFeedback.criteria.map((criterion, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              sx={{ py: 0.5 }}
            >
              <Typography variant="body2">
                {criterion.key ? `Q${criterion.key} — ${stripKeyPrefix(criterion.name, criterion.key)}` : criterion.name}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {criterion.score} / {criterion.max_score}
              </Typography>
            </Box>
          ))}
          <Divider sx={{ my: 1 }} />
          <Box display="flex" justifyContent="space-between" sx={{ py: 0.5 }}>
            <Typography variant="body1" fontWeight="bold">
              Total
            </Typography>
            <Typography variant="body1" fontWeight="bold" color="primary">
              {parsedFeedback.score} / {totalMaxScore}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default StructuredFeedback;
